import React, { useState, useEffect } from 'react';
import { Eye, UserPlus, CreditCard, TrendingUp, BarChart3, Calendar, RefreshCw, QrCode, Copy, Download, Users, DollarSign } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { adminPromoterMetricsAPI, adminPromotersAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const PromoterMetrics = () => {
  const [overview, setOverview] = useState(null);
  const [promoters, setPromoters] = useState([]);
  const [selectedPromoter, setSelectedPromoter] = useState(null);
  const [promoterMetrics, setPromoterMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [topMetric, setTopMetric] = useState('visits');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedQrPromoter, setSelectedQrPromoter] = useState(null);
  const [registeredUsersModalOpen, setRegisteredUsersModalOpen] = useState(false);
  const [paidUsersModalOpen, setPaidUsersModalOpen] = useState(false);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [paidUsers, setPaidUsers] = useState([]);
  const [loadingRegisteredUsers, setLoadingRegisteredUsers] = useState(false);
  const [loadingPaidUsers, setLoadingPaidUsers] = useState(false);

  useEffect(() => {
    loadData();
  }, [dateFrom, dateTo, topMetric]);

  const loadData = async () => {
    try {
      setIsRefreshing(true);
      setLoading(true);
      
      // Cargar overview
      const overviewResponse = await adminPromoterMetricsAPI.getOverview({
        date_from: dateFrom,
        date_to: dateTo,
      });
      setOverview(overviewResponse.data);

      // Cargar top promotores
      await loadTopPromoters();
    } catch (error) {
      console.error('Error loading metrics:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar las métricas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      // Mantener la animación un poco más para que se vea bien
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const loadTopPromoters = async () => {
    try {
      const response = await adminPromoterMetricsAPI.getTopPromoters({
        metric: topMetric,
        limit: 10,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setPromoters(response.data);
    } catch (error) {
      console.error('Error loading top promoters:', error);
    }
  };

  const loadPromoterMetrics = async (promoterId) => {
    try {
      const response = await adminPromoterMetricsAPI.getPromoterMetrics(promoterId, {
        date_from: dateFrom,
        date_to: dateTo,
      });
      setPromoterMetrics(response.data);
      setSelectedPromoter(promoterId);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar las métricas del promotor",
        variant: "destructive",
      });
    }
  };

  // Obtener URL base del frontend
  const getFrontendUrl = () => {
    return window.location.origin;
  };

  // Generar URL con parámetro UTM para el promotor
  const getPromoterUrl = (promoterId) => {
    const frontendUrl = getFrontendUrl();
    return `${frontendUrl}/?utm_source=${promoterId}`;
  };

  const handleShowQr = (promoter) => {
    setSelectedQrPromoter(promoter);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = () => {
    setQrModalOpen(false);
    setSelectedQrPromoter(null);
  };

  const loadRegisteredUsers = async (promoterId) => {
    try {
      setLoadingRegisteredUsers(true);
      const response = await adminPromoterMetricsAPI.getRegisteredUsers(promoterId, {
        date_from: dateFrom,
        date_to: dateTo,
      });
      setRegisteredUsers(response.data.users || []);
      setRegisteredUsersModalOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los usuarios registrados",
        variant: "destructive",
      });
    } finally {
      setLoadingRegisteredUsers(false);
    }
  };

  const loadPaidUsers = async (promoterId) => {
    try {
      setLoadingPaidUsers(true);
      const response = await adminPromoterMetricsAPI.getPaidUsers(promoterId, {
        date_from: dateFrom,
        date_to: dateTo,
      });
      setPaidUsers(response.data.users || []);
      setPaidUsersModalOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los usuarios que pagaron",
        variant: "destructive",
      });
    } finally {
      setLoadingPaidUsers(false);
    }
  };

  const handleCopyUrl = (promoterId) => {
    const url = getPromoterUrl(promoterId);
    navigator.clipboard.writeText(url);
    toast({
      title: "Copiado",
      description: "URL copiada al portapapeles",
    });
  };

  if (loading && !overview) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-white">Cargando estadísticas...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <Toaster />
      
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Estadísticas de Promotores</h2>
        <p className="text-[#A2A2A2]">Métricas de visitas, registros, pagos y conversión</p>
      </div>

      {/* Date Range Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-end">
        <div className="flex-1 w-full sm:w-auto">
          <Label htmlFor="date_from" className="text-white text-sm mb-2 block">
            Desde
          </Label>
          <Input
            id="date_from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>
        <div className="flex-1 w-full sm:w-auto">
          <Label htmlFor="date_to" className="text-white text-sm mb-2 block">
            Hasta
          </Label>
          <Input
            id="date_to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>
        <Button
          onClick={loadData}
          disabled={isRefreshing}
          className="bg-white text-black hover:bg-white/90 w-full sm:w-auto transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          <RefreshCw 
            className={`w-4 h-4 mr-2 transition-transform duration-500 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          <span className="relative">
            {isRefreshing ? 'Actualizando...' : 'Actualizar'}
            {isRefreshing && (
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/30 opacity-75"></span>
              </span>
            )}
          </span>
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <p className="text-[#A2A2A2] text-sm">Total Promotores</p>
          </div>
          <p className="text-2xl font-bold text-white">{overview?.total_promoters || 0}</p>
          <p className="text-xs text-[#A2A2A2] mt-1">{overview?.active_promoters || 0} activos</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <p className="text-[#A2A2A2] text-sm">Total Visitas</p>
          </div>
          <p className="text-2xl font-bold text-white">{overview?.total_visits || 0}</p>
          <p className="text-xs text-[#A2A2A2] mt-1">En el período seleccionado</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <p className="text-[#A2A2A2] text-sm">Total Registros</p>
          </div>
          <p className="text-2xl font-bold text-white">{overview?.total_registrations || 0}</p>
          <p className="text-xs text-[#A2A2A2] mt-1">
            {overview?.overall_conversion_rate || 0}% conversión
          </p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <p className="text-[#A2A2A2] text-sm">Total Pagos</p>
          </div>
          <p className="text-2xl font-bold text-white">{overview?.total_payments || 0}</p>
          <p className="text-xs text-[#A2A2A2] mt-1">
            ${overview?.total_revenue || 0} MXN
          </p>
        </div>
      </div>

      {/* Top Promoters Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h3 className="text-xl font-bold text-white">Top Promotores</h3>
          <Select value={topMetric} onValueChange={setTopMetric}>
            <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-white/10">
              <SelectItem value="visits" className="text-white">Por Visitas</SelectItem>
              <SelectItem value="registrations" className="text-white">Por Registros</SelectItem>
              <SelectItem value="payments" className="text-white">Por Pagos</SelectItem>
              <SelectItem value="revenue" className="text-white">Por Ingresos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">
                    Promotor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">
                    Visitas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">
                    Registros
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">
                    Pagos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">
                    Conversión
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">
                    Ver en Dashboard
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {promoters.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-[#A2A2A2]">
                      No hay promotores registrados
                    </td>
                  </tr>
                ) : (
                  promoters.map((promoter) => {
                    const visits = promoter.visits_count || 0;
                    const registrations = promoter.registrations_count || 0;
                    const payments = promoter.payments_count || 0;
                    const revenue = promoter.revenue_sum || 0;
                    const conversionRate = visits > 0 ? ((registrations / visits) * 100).toFixed(2) : '0.00';
                    const showInDashboard = promoter.show_in_dashboard || false;

                    const handleToggleShowInDashboard = async (checked) => {
                      try {
                        const response = await adminPromotersAPI.updateShowInDashboard(promoter.id, checked);
                        if (response.success) {
                          // Recargar los promotores para obtener los datos actualizados
                          await loadTopPromoters();
                          toast({
                            title: "Actualizado",
                            description: `Visibilidad en dashboard ${checked ? 'activada' : 'desactivada'}`,
                          });
                        }
                      } catch (error) {
                        toast({
                          title: "Error",
                          description: error.message || "No se pudo actualizar la visibilidad",
                          variant: "destructive",
                        });
                      }
                    };

                    return (
                      <tr key={promoter.id} className="hover:bg-white/5">
                        <td className="px-6 py-4 whitespace-nowrap text-white">
                          {promoter.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-[#A2A2A2]">
                          {visits}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-[#A2A2A2]">
                          {registrations}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-[#A2A2A2]">
                          {payments} {revenue > 0 && `($${revenue.toFixed(2)})`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-[#A2A2A2]">
                          {conversionRate}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center">
                            <Switch
                              checked={showInDashboard}
                              onCheckedChange={handleToggleShowInDashboard}
                              className="data-[state=checked]:bg-white data-[state=unchecked]:bg-white/20"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleShowQr(promoter)}
                              className="text-white hover:bg-white/10"
                              title="Ver QR"
                            >
                              <QrCode className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => loadPromoterMetrics(promoter.id)}
                              className="text-white hover:bg-white/10 w-full sm:w-auto"
                            >
                              <BarChart3 className="w-4 h-4 sm:mr-2" />
                              <span className="hidden sm:inline">Ver Detalles</span>
                              <span className="sm:hidden">Detalles</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Promoter Details Modal */}
      <Dialog 
        open={!!promoterMetrics && !!selectedPromoter} 
        onOpenChange={(open) => {
          if (!open) {
            setPromoterMetrics(null);
            setSelectedPromoter(null);
          }
        }}
      >
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Métricas Detalladas - {promoterMetrics?.promoter?.name}
            </DialogTitle>
          </DialogHeader>
          
          {promoterMetrics && (
            <>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-black border border-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-[#A2A2A2]" />
                    <p className="text-[#A2A2A2] text-sm">Visitas</p>
                  </div>
                  <p className="text-2xl font-bold text-white">{promoterMetrics.metrics.visits}</p>
                </div>

                <div className="bg-black border border-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus className="w-4 h-4 text-[#A2A2A2]" />
                    <p className="text-[#A2A2A2] text-sm">Registros</p>
                  </div>
                  <p className="text-2xl font-bold text-white">{promoterMetrics.metrics.registrations}</p>
                  <p className="text-xs text-[#A2A2A2] mt-1">
                    {promoterMetrics.metrics.conversion_rate}% conversión
                  </p>
                  {promoterMetrics.metrics.registrations > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadRegisteredUsers(selectedPromoter)}
                      disabled={loadingRegisteredUsers}
                      className="mt-2 w-full border-white/10 text-white hover:bg-white/10 text-xs"
                    >
                      <Users className="w-3 h-3 mr-1" />
                      {loadingRegisteredUsers ? 'Cargando...' : 'Ver Usuarios'}
                    </Button>
                  )}
                </div>

                <div className="bg-black border border-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-[#A2A2A2]" />
                    <p className="text-[#A2A2A2] text-sm">Pagos</p>
                  </div>
                  <p className="text-2xl font-bold text-white">{promoterMetrics.metrics.payments}</p>
                  <p className="text-xs text-[#A2A2A2] mt-1">
                    {promoterMetrics.metrics.payment_rate}% tasa de pago
                  </p>
                  {promoterMetrics.metrics.payments > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadPaidUsers(selectedPromoter)}
                      disabled={loadingPaidUsers}
                      className="mt-2 w-full border-white/10 text-white hover:bg-white/10 text-xs"
                    >
                      <DollarSign className="w-3 h-3 mr-1" />
                      {loadingPaidUsers ? 'Cargando...' : 'Ver Usuarios'}
                    </Button>
                  )}
                </div>

                <div className="bg-black border border-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-[#A2A2A2]" />
                    <p className="text-[#A2A2A2] text-sm">Ingresos</p>
                  </div>
                  <p className="text-2xl font-bold text-white">${promoterMetrics.metrics.revenue}</p>
                  <p className="text-xs text-[#A2A2A2] mt-1">MXN</p>
                </div>
              </div>

              {/* Charts placeholder */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-black border border-white/10 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-4">Visitas por Día</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {promoterMetrics.visits_by_day && promoterMetrics.visits_by_day.length > 0 ? (
                      promoterMetrics.visits_by_day.map((day, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-[#A2A2A2] text-sm">{day.date}</span>
                          <span className="text-white font-medium">{day.count} visitas</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-[#A2A2A2] text-sm">No hay datos disponibles</p>
                    )}
                  </div>
                </div>

                <div className="bg-black border border-white/10 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-4">Conversiones por Día</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {promoterMetrics.conversions_by_day && promoterMetrics.conversions_by_day.length > 0 ? (
                      promoterMetrics.conversions_by_day.map((day, index) => (
                        <div key={index} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                          <span className="text-[#A2A2A2] text-sm">{day.date}</span>
                          <div className="flex gap-4">
                            <span className="text-blue-400 text-sm">{day.registrations || 0} reg.</span>
                            <span className="text-green-400 text-sm">{day.payments || 0} pagos</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-[#A2A2A2] text-sm">No hay datos disponibles</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Código QR - {selectedQrPromoter?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedQrPromoter && (
            <div className="space-y-4">
              <div className="flex justify-center bg-white p-4 rounded-lg" data-qr-svg>
                <QRCodeSVG
                  value={getPromoterUrl(selectedQrPromoter.id)}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white text-sm">URL del Promotor</Label>
                <div className="flex gap-2">
                  <Input
                    value={getPromoterUrl(selectedQrPromoter.id)}
                    readOnly
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyUrl(selectedQrPromoter.id)}
                    className="border-white/10 text-white"
                    title="Copiar URL"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Obtener el SVG del QR
                    const svgElement = document.querySelector('[data-qr-svg] svg');
                    if (svgElement) {
                      const svgData = new XMLSerializer().serializeToString(svgElement);
                      const canvas = document.createElement('canvas');
                      const ctx = canvas.getContext('2d');
                      const img = new Image();
                      img.onload = () => {
                        canvas.width = img.width;
                        canvas.height = img.height;
                        ctx.drawImage(img, 0, 0);
                        const url = canvas.toDataURL('image/png');
                        const link = document.createElement('a');
                        link.download = `promoter-${selectedQrPromoter.id}-qr.png`;
                        link.href = url;
                        link.click();
                      };
                      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                    }
                  }}
                  className="border-white/10 text-white flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar QR
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCloseQrModal}
                  className="border-white/10 text-white"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Registered Users Modal */}
      <Dialog open={registeredUsersModalOpen} onOpenChange={setRegisteredUsersModalOpen}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Usuarios Registrados - {promoterMetrics?.promoter?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <p className="text-[#A2A2A2] text-sm mb-4">
              Total: {registeredUsers.length} usuario(s) registrado(s)
            </p>
            
            {registeredUsers.length === 0 ? (
              <p className="text-[#A2A2A2] text-center py-8">No hay usuarios registrados en el período seleccionado</p>
            ) : (
              <div className="bg-black border border-white/10 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Nombre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Teléfono</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Fecha de Registro</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {registeredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-white/5">
                          <td className="px-4 py-3 text-white">{user.name || 'N/A'}</td>
                          <td className="px-4 py-3 text-[#A2A2A2]">{user.email || 'N/A'}</td>
                          <td className="px-4 py-3 text-[#A2A2A2]">{user.phone || 'N/A'}</td>
                          <td className="px-4 py-3 text-[#A2A2A2]">{user.registered_at || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Paid Users Modal */}
      <Dialog open={paidUsersModalOpen} onOpenChange={setPaidUsersModalOpen}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Usuarios que Pagaron - {promoterMetrics?.promoter?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <p className="text-[#A2A2A2] text-sm mb-4">
              Total: {paidUsers.length} usuario(s) con pago
            </p>
            
            {paidUsers.length === 0 ? (
              <p className="text-[#A2A2A2] text-center py-8">No hay usuarios que hayan pagado en el período seleccionado</p>
            ) : (
              <div className="bg-black border border-white/10 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Nombre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Teléfono</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Fecha de Pago</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#A2A2A2] uppercase">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {paidUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-white/5">
                          <td className="px-4 py-3 text-white">{user.name || 'N/A'}</td>
                          <td className="px-4 py-3 text-[#A2A2A2]">{user.email || 'N/A'}</td>
                          <td className="px-4 py-3 text-[#A2A2A2]">{user.phone || 'N/A'}</td>
                          <td className="px-4 py-3 text-[#A2A2A2]">{user.paid_at || 'N/A'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              user.subscription_status === 'active' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {user.subscription_status === 'active' ? 'Activo' : 'Prueba'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoterMetrics;

